/**
 * Notifications Store - User notification preferences
 *
 * Versione GraphQL - tutti i dati persistiti nel database
 *
 * Gestisce:
 * - Canali di notifica (email, push, in-app)
 * - Preferenze per tipo di notifica
 * - Orari "non disturbare"
 * - Impostazioni digest
 * - Formato email
 */

import { create } from 'zustand';
import { graphqlFetch } from '../database/graphqlClient';
import type { InAppNotification } from '../ai/engine/types';

// =============================================================================
// GRAPHQL HELPER (uses centralized client)
// =============================================================================

async function graphqlRequest<T>(query: string, variables?: Record<string, unknown>): Promise<T | null> {
  try {
    const response = await graphqlFetch<T>(query, variables);
    if (response.errors) {
      console.error('GraphQL errors:', response.errors);
      return null;
    }
    return response.data || null;
  } catch (error) {
    console.error('GraphQL request error:', error);
    return null;
  }
}

// =============================================================================
// TYPES
// =============================================================================

/**
 * Canali di notifica disponibili
 */
export type NotificationChannel = 'email' | 'push' | 'inApp';

/**
 * Tipi di evento notifica supportati
 * (Named NotificationEventType to avoid collision with domain NotificationType)
 */
export type NotificationEventType =
  | 'task_assigned'
  | 'task_completed'
  | 'task_due_soon'
  | 'task_overdue'
  | 'new_message'
  | 'missed_call'
  | 'new_email'
  | 'invoice_due_soon'
  | 'invoice_overdue'
  | 'payment_received'
  | 'milestone_approaching'
  | 'calendar_event'
  | 'team_member_added'
  | 'bank_transaction_received'
  | 'invoice_received'
  | 'invoice_sent'
  | 'mention'
  | 'comment_reply';

/**
 * Configurazione notifiche per tipo
 */
export interface NotificationTypeConfig {
  type: NotificationEventType;
  label: string;
  email: boolean;
  push: boolean;
  inApp: boolean;
}

/**
 * Giorni della settimana per orario non disturbare
 */
export type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

/**
 * Configurazione orario non disturbare
 */
export interface DoNotDisturbConfig {
  enabled: boolean;
  fromTime: string; // HH:mm format
  toTime: string;   // HH:mm format
  activeDays: DayOfWeek[];
}

/**
 * Configurazione digest
 */
export interface DigestConfig {
  dailyEnabled: boolean;
  dailyTime: string; // HH:mm format
  weeklyEnabled: boolean;
  weeklyDay: DayOfWeek;
  weeklyTime: string; // HH:mm format
}

/**
 * Formato email
 */
export type EmailFormat = 'html' | 'text';

/**
 * Lingua email
 */
export type EmailLanguage = 'it' | 'en';

/**
 * Push permission status
 */
export type PushPermissionStatus = 'default' | 'granted' | 'denied';

// =============================================================================
// DEFAULT VALUES
// =============================================================================

const DEFAULT_TYPE_CONFIGS: NotificationTypeConfig[] = [
  { type: 'task_assigned', label: 'Task assegnato', email: true, push: true, inApp: true },
  { type: 'task_completed', label: 'Task completato', email: false, push: false, inApp: true },
  { type: 'task_due_soon', label: 'Task in scadenza (1 giorno)', email: true, push: true, inApp: true },
  { type: 'task_overdue', label: 'Task scaduto', email: true, push: true, inApp: true },
  { type: 'new_message', label: 'Nuovo messaggio', email: false, push: true, inApp: true },
  { type: 'missed_call', label: 'Chiamata persa', email: true, push: true, inApp: true },
  { type: 'new_email', label: 'Nuova email', email: false, push: false, inApp: true },
  { type: 'invoice_due_soon', label: 'Fattura in scadenza', email: true, push: false, inApp: true },
  { type: 'invoice_overdue', label: 'Fattura scaduta', email: true, push: true, inApp: true },
  { type: 'payment_received', label: 'Pagamento ricevuto', email: true, push: false, inApp: true },
  { type: 'milestone_approaching', label: 'Milestone in arrivo', email: true, push: true, inApp: true },
  { type: 'calendar_event', label: 'Evento calendario', email: true, push: true, inApp: true },
  { type: 'team_member_added', label: 'Membro team aggiunto', email: true, push: false, inApp: true },
  { type: 'bank_transaction_received', label: 'Transazione bancaria ricevuta', email: false, push: false, inApp: true },
  { type: 'invoice_received', label: 'Fattura ricevuta', email: true, push: false, inApp: true },
  { type: 'invoice_sent', label: 'Fattura inviata', email: false, push: false, inApp: true },
  { type: 'mention', label: 'Menzione', email: true, push: true, inApp: true },
  { type: 'comment_reply', label: 'Risposta a commento', email: true, push: true, inApp: true },
];

const DEFAULT_DO_NOT_DISTURB: DoNotDisturbConfig = {
  enabled: false,
  fromTime: '22:00',
  toTime: '08:00',
  activeDays: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
};

const DEFAULT_DIGEST: DigestConfig = {
  dailyEnabled: false,
  dailyTime: '09:00',
  weeklyEnabled: false,
  weeklyDay: 'mon',
  weeklyTime: '09:00',
};

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const QUERIES = {
  notificationSettings: `
    query GetNotificationSettings($userId: String) {
      notificationSettings(userId: $userId) {
        id
        userId
        emailEnabled
        pushEnabled
        inAppEnabled
        typeConfigs {
          notificationType
          label
          email
          push
          inApp
        }
        doNotDisturb {
          enabled
          fromTime
          toTime
          activeDays
        }
        digest {
          dailyEnabled
          dailyTime
          weeklyEnabled
          weeklyDay
          weeklyTime
        }
        emailFormat
        emailLanguage
        createdAt
        updatedAt
      }
    }
  `,
};

const MUTATIONS = {
  saveNotificationSettings: `
    mutation SaveNotificationSettings($input: NotificationSettingsInput!, $userId: String) {
      saveNotificationSettings(input: $input, userId: $userId) {
        id
        userId
        emailEnabled
        pushEnabled
        inAppEnabled
        typeConfigs {
          notificationType
          label
          email
          push
          inApp
        }
        doNotDisturb {
          enabled
          fromTime
          toTime
          activeDays
        }
        digest {
          dailyEnabled
          dailyTime
          weeklyEnabled
          weeklyDay
          weeklyTime
        }
        emailFormat
        emailLanguage
        createdAt
        updatedAt
      }
    }
  `,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Declare browser globals for environments where DOM types are not available
declare const window: { Notification?: { requestPermission: () => Promise<string>; permission: string } } | undefined;
declare const navigator: { serviceWorker?: unknown } | undefined;

/**
 * Check if browser supports push notifications
 */
function supportsPushNotifications(): boolean {
  if (typeof window === 'undefined') return false;
  return !!window?.Notification && typeof navigator !== 'undefined' && !!navigator?.serviceWorker;
}

/**
 * Request push notification permission
 */
async function requestPushPermission(): Promise<PushPermissionStatus> {
  if (!supportsPushNotifications() || !window?.Notification) {
    return 'denied';
  }
  const result = await window.Notification.requestPermission();
  return result as PushPermissionStatus;
}

/**
 * Get current push permission status
 */
function getPushPermissionStatus(): PushPermissionStatus {
  if (!supportsPushNotifications() || !window?.Notification) {
    return 'denied';
  }
  return window.Notification.permission as PushPermissionStatus;
}

// Map GraphQL response to local types
function mapGraphQLTypeConfig(c: {
  notificationType: string;
  label: string;
  email: boolean;
  push: boolean;
  inApp: boolean;
}): NotificationTypeConfig {
  return {
    type: c.notificationType as NotificationEventType,
    label: c.label,
    email: c.email,
    push: c.push,
    inApp: c.inApp,
  };
}

function mapGraphQLDoNotDisturb(d: {
  enabled: boolean;
  fromTime: string;
  toTime: string;
  activeDays: string[];
}): DoNotDisturbConfig {
  return {
    enabled: d.enabled,
    fromTime: d.fromTime,
    toTime: d.toTime,
    activeDays: d.activeDays as DayOfWeek[],
  };
}

function mapGraphQLDigest(d: {
  dailyEnabled: boolean;
  dailyTime: string;
  weeklyEnabled: boolean;
  weeklyDay: string;
  weeklyTime: string;
}): DigestConfig {
  return {
    dailyEnabled: d.dailyEnabled,
    dailyTime: d.dailyTime,
    weeklyEnabled: d.weeklyEnabled,
    weeklyDay: d.weeklyDay as DayOfWeek,
    weeklyTime: d.weeklyTime,
  };
}

// =============================================================================
// STORE STATE
// =============================================================================

export interface NotificationsState {
  // Live notifications (bell icon feed)
  liveNotifications: InAppNotification[];

  // Canali globali
  emailEnabled: boolean;
  pushEnabled: boolean;
  inAppEnabled: boolean;
  pushPermissionStatus: PushPermissionStatus;

  // Preferenze per tipo
  typeConfigs: NotificationTypeConfig[];

  // Non disturbare
  doNotDisturb: DoNotDisturbConfig;

  // Digest
  digest: DigestConfig;

  // Formato email
  emailFormat: EmailFormat;
  emailLanguage: EmailLanguage;

  // Status
  initialized: boolean;
  isSaving: boolean;
  lastSaved: string | null;

  // Actions - Live notifications
  addNotification: (notification: InAppNotification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  unreadCount: () => number;

  // Actions - Load
  loadData: () => Promise<void>;

  // Actions - Canali
  setEmailEnabled: (enabled: boolean) => void;
  setPushEnabled: (enabled: boolean) => Promise<void>;
  setInAppEnabled: (enabled: boolean) => void;

  // Actions - Tipi notifica
  setTypeChannelEnabled: (type: NotificationEventType, channel: NotificationChannel, enabled: boolean) => void;
  setAllChannelsForType: (type: NotificationEventType, enabled: boolean) => void;
  setAllTypesForChannel: (channel: NotificationChannel, enabled: boolean) => void;

  // Actions - Non disturbare
  setDoNotDisturbEnabled: (enabled: boolean) => void;
  setDoNotDisturbTimes: (fromTime: string, toTime: string) => void;
  setDoNotDisturbDays: (days: DayOfWeek[]) => void;
  toggleDoNotDisturbDay: (day: DayOfWeek) => void;

  // Actions - Digest
  setDailyDigest: (enabled: boolean, time?: string) => void;
  setWeeklyDigest: (enabled: boolean, day?: DayOfWeek, time?: string) => void;

  // Actions - Email format
  setEmailFormat: (format: EmailFormat) => void;
  setEmailLanguage: (language: EmailLanguage) => void;

  // Actions - Persistence
  savePreferences: () => Promise<void>;
  resetToDefaults: () => void;

  // Selectors
  isChannelEnabledForType: (type: NotificationEventType, channel: NotificationChannel) => boolean;
  getActiveChannelsCount: (type: NotificationEventType) => number;
  isInDoNotDisturbPeriod: () => boolean;
}

// =============================================================================
// STORE
// =============================================================================

export const useNotificationsStore = create<NotificationsState>()((set, get) => ({
  // Initial state
  liveNotifications: [],
  emailEnabled: true,
  pushEnabled: false,
  inAppEnabled: true,
  pushPermissionStatus: getPushPermissionStatus(),
  typeConfigs: [...DEFAULT_TYPE_CONFIGS],
  doNotDisturb: { ...DEFAULT_DO_NOT_DISTURB },
  digest: { ...DEFAULT_DIGEST },
  emailFormat: 'html',
  emailLanguage: 'it',
  initialized: false,
  isSaving: false,
  lastSaved: null,

  // Live notification actions
  addNotification: (notification) => {
    const state = get();
    // Respect DnD
    if (state.isInDoNotDisturbPeriod()) return;
    // Respect inApp global toggle
    if (!state.inAppEnabled) return;

    set((s) => ({
      liveNotifications: [notification, ...s.liveNotifications].slice(0, 500),
    }));
  },

  markAsRead: (id) => {
    set((s) => ({
      liveNotifications: s.liveNotifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      ),
    }));
  },

  markAllAsRead: () => {
    set((s) => ({
      liveNotifications: s.liveNotifications.map((n) => ({ ...n, read: true })),
    }));
  },

  clearNotifications: () => {
    set({ liveNotifications: [] });
  },

  unreadCount: () => {
    return get().liveNotifications.filter((n) => !n.read).length;
  },

  // Load data from GraphQL
  loadData: async () => {
    const data = await graphqlRequest<{ notificationSettings: any }>(
      QUERIES.notificationSettings,
      { userId: null }
    );

    if (data?.notificationSettings) {
      const s = data.notificationSettings;
      set({
        emailEnabled: s.emailEnabled,
        pushEnabled: s.pushEnabled,
        inAppEnabled: s.inAppEnabled,
        typeConfigs: s.typeConfigs.map(mapGraphQLTypeConfig),
        doNotDisturb: mapGraphQLDoNotDisturb(s.doNotDisturb),
        digest: mapGraphQLDigest(s.digest),
        emailFormat: s.emailFormat as EmailFormat,
        emailLanguage: s.emailLanguage as EmailLanguage,
        initialized: true,
        lastSaved: s.updatedAt,
      });
    } else {
      // No settings in DB yet, use defaults
      set({ initialized: true });
    }
  },

  // Channel actions
  setEmailEnabled: (enabled) => set({ emailEnabled: enabled }),

  setPushEnabled: async (enabled) => {
    if (enabled) {
      const permission = await requestPushPermission();
      set({
        pushEnabled: permission === 'granted',
        pushPermissionStatus: permission,
      });
    } else {
      set({ pushEnabled: false });
    }
  },

  setInAppEnabled: (enabled) => set({ inAppEnabled: enabled }),

  // Type config actions
  setTypeChannelEnabled: (type, channel, enabled) => {
    set((state) => ({
      typeConfigs: state.typeConfigs.map((config) =>
        config.type === type ? { ...config, [channel]: enabled } : config
      ),
    }));
  },

  setAllChannelsForType: (type, enabled) => {
    set((state) => ({
      typeConfigs: state.typeConfigs.map((config) =>
        config.type === type
          ? { ...config, email: enabled, push: enabled, inApp: enabled }
          : config
      ),
    }));
  },

  setAllTypesForChannel: (channel, enabled) => {
    set((state) => ({
      typeConfigs: state.typeConfigs.map((config) => ({
        ...config,
        [channel]: enabled,
      })),
    }));
  },

  // Do Not Disturb actions
  setDoNotDisturbEnabled: (enabled) => {
    set((state) => ({
      doNotDisturb: { ...state.doNotDisturb, enabled },
    }));
  },

  setDoNotDisturbTimes: (fromTime, toTime) => {
    set((state) => ({
      doNotDisturb: { ...state.doNotDisturb, fromTime, toTime },
    }));
  },

  setDoNotDisturbDays: (days) => {
    set((state) => ({
      doNotDisturb: { ...state.doNotDisturb, activeDays: days },
    }));
  },

  toggleDoNotDisturbDay: (day) => {
    set((state) => {
      const currentDays = state.doNotDisturb.activeDays;
      const newDays = currentDays.includes(day)
        ? currentDays.filter((d) => d !== day)
        : [...currentDays, day];
      return {
        doNotDisturb: { ...state.doNotDisturb, activeDays: newDays },
      };
    });
  },

  // Digest actions
  setDailyDigest: (enabled, time) => {
    set((state) => ({
      digest: {
        ...state.digest,
        dailyEnabled: enabled,
        ...(time && { dailyTime: time }),
      },
    }));
  },

  setWeeklyDigest: (enabled, day, time) => {
    set((state) => ({
      digest: {
        ...state.digest,
        weeklyEnabled: enabled,
        ...(day && { weeklyDay: day }),
        ...(time && { weeklyTime: time }),
      },
    }));
  },

  // Email format actions
  setEmailFormat: (format) => set({ emailFormat: format }),
  setEmailLanguage: (language) => set({ emailLanguage: language }),

  // Persistence actions
  savePreferences: async () => {
    const state = get();
    set({ isSaving: true });

    const input = {
      email_enabled: state.emailEnabled,
      push_enabled: state.pushEnabled,
      in_app_enabled: state.inAppEnabled,
      type_configs: state.typeConfigs.map((c) => ({
        notification_type: c.type,
        label: c.label,
        email: c.email,
        push: c.push,
        in_app: c.inApp,
      })),
      do_not_disturb: {
        enabled: state.doNotDisturb.enabled,
        from_time: state.doNotDisturb.fromTime,
        to_time: state.doNotDisturb.toTime,
        active_days: state.doNotDisturb.activeDays,
      },
      digest: {
        daily_enabled: state.digest.dailyEnabled,
        daily_time: state.digest.dailyTime,
        weekly_enabled: state.digest.weeklyEnabled,
        weekly_day: state.digest.weeklyDay,
        weekly_time: state.digest.weeklyTime,
      },
      email_format: state.emailFormat,
      email_language: state.emailLanguage,
    };

    const result = await graphqlRequest<{ saveNotificationSettings: any }>(
      MUTATIONS.saveNotificationSettings,
      { input, userId: null }
    );

    if (result?.saveNotificationSettings) {
      set({
        isSaving: false,
        lastSaved: new Date().toISOString(),
      });
    } else {
      set({ isSaving: false });
    }
  },

  resetToDefaults: () => {
    set({
      emailEnabled: true,
      pushEnabled: false,
      inAppEnabled: true,
      typeConfigs: [...DEFAULT_TYPE_CONFIGS],
      doNotDisturb: { ...DEFAULT_DO_NOT_DISTURB },
      digest: { ...DEFAULT_DIGEST },
      emailFormat: 'html',
      emailLanguage: 'it',
    });
  },

  // Selectors
  isChannelEnabledForType: (type, channel) => {
    const config = get().typeConfigs.find((c) => c.type === type);
    if (!config) return false;
    return config[channel];
  },

  getActiveChannelsCount: (type) => {
    const config = get().typeConfigs.find((c) => c.type === type);
    if (!config) return 0;
    return [config.email, config.push, config.inApp].filter(Boolean).length;
  },

  isInDoNotDisturbPeriod: () => {
    const state = get();
    if (!state.doNotDisturb.enabled) return false;

    const now = new Date();
    const dayMap: Record<number, DayOfWeek> = {
      0: 'sun',
      1: 'mon',
      2: 'tue',
      3: 'wed',
      4: 'thu',
      5: 'fri',
      6: 'sat',
    };
    const currentDay = dayMap[now.getDay()];

    if (!state.doNotDisturb.activeDays.includes(currentDay)) {
      return false;
    }

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [fromHours, fromMinutes] = state.doNotDisturb.fromTime.split(':').map(Number);
    const [toHours, toMinutes] = state.doNotDisturb.toTime.split(':').map(Number);
    const fromTime = fromHours * 60 + fromMinutes;
    const toTime = toHours * 60 + toMinutes;

    // Handle overnight periods (e.g., 22:00 - 08:00)
    if (fromTime > toTime) {
      return currentTime >= fromTime || currentTime < toTime;
    }

    return currentTime >= fromTime && currentTime < toTime;
  },
}));

// Initialize - load data from GraphQL on first access
if (typeof window !== 'undefined') {
  setTimeout(() => {
    useNotificationsStore.getState().loadData();
  }, 100);
}
